// apps/share-web/src/chart/chart-buttons.ts
//
// Кнопки хрома чарта — ДС-компонент <bw-button>, и только он.
//
// Пункт приёмки A1-2, дословно: «А кнопка должна быть кнопкой по дизайн-системе,
// а непонятно чем. Снова отходим от дизайн-системы.» До этого хром чарта нёс три
// рукописных кнопки со своими правилами: `.cw-icon` (30×28, ⤢ и ✕), `.cw-nav`
// (44×44, ‹ ›) и `.pv-chart-btn` (пилюля «Chart & Lyrics»). Каждая рисовала свой
// плоский кап из `--ctrl` + `--ctrl-hair`, а рядом, в том же микшере, стояли
// настоящие ДС-капы (M/S на дорожках, prev/next транспорта) — градиент
// `--hw-cap-*` с приподнятой тенью. Правило пакета
// (packages/ui-components/README.md): «never hand-roll UI that duplicates a
// `bw-*` component».
//
// Модуль отдельный, а не по функции в каждой поверхности, потому что таких
// поверхностей ТРИ (панель, слой, шапка плеера), и правил, которые легко
// потерять в четвёртой, тоже три:
//
//  1. ГЛИФ ЕДЕТ В СЛОТ `icon-start`, а не в подпись. bw-button.css схлопывает
//     кнопку в квадрат селектором `bw-button:not(:has(.bw-button-label:not(:empty)))`
//     — то есть только когда слот подписи ПУСТ. Глиф, положенный в подпись,
//     оставляет прямоугольник с боковыми отступами; десктоп на это уже наступал
//     (app/renderer/ui/import/import-tracks-table.js, `iconSlot`).
//  2. ДОСТУПНОЕ ИМЯ ОБЯЗАТЕЛЬНО у кнопки-глифа: скринридер объявил бы «✕» и «›».
//     Ставится так же, как это делает вся остальная ДС — `aria-label` на ХОСТЕ
//     (bw-transport.ts:69,79,115; bw-track-cell.ts:97,107,126;
//     setlist-share-modal.js:441). ⚠️ Оговорка: настоящая кнопка живёт ВНУТРИ
//     компонента (`.bw-button-inner`), и `aria-label` на хосте до неё не
//     доходит. Это дефект самой ДС, общий для всех её потребителей, и лечится он
//     проброс-свойством в bw-button, а не костылём здесь — иначе у одной
//     поверхности имя будет попадать в кнопку, а у остальных нет.
//  3. СОБЫТИЕ — `bw-click`, а не `click`. Хост — не кнопка, и click на нём
//     ничего не значит.
//
// Размеры берутся из шкалы ДС (sm 28 / md 36 / lg 48), а не подгоняются CSS'ом
// из потребителя: панельные иконки — курсор (sm), всё в полноэкранном слое —
// палец (lg).

import '@bandway/ui-components';
// Кнопка ДС живёт в СВЕТЛОМ DOM и без своего внешнего CSS рендерится голым
// <button> без капа. mount.ts этот файл уже тянет, но модуль обязан быть
// самодостаточным: он попадает в граф из трёх мест, и «кто-то выше уже
// импортировал» — это не гарантия, а совпадение. Тот же приём у ui/app-header.ts.
import '@bandway/ui-components/styles/bw-button.css';

export type BwSize = 'sm' | 'md' | 'lg';
export type BwShape = 'rounded' | 'pill' | 'circle' | 'square';

/**
 * `<bw-button>` как его видит потребитель. Свойства Lit ставятся ПРИСВАИВАНИЕМ
 * и сами отражаются в атрибуты; приведение нужно потому, что в этом проекте
 * глобальный `Element` приходит из `@cloudflare/workers-types`, а не из DOM, и
 * `HTMLElementTagNameMap` на него не действует (тот же шов у transport.ts).
 */
export type BwButtonEl = HTMLElement & {
  size: BwSize;
  shape: BwShape;
  block: boolean;
  /** Внешнее состояние «нажата». НЕ самофлипающийся `pressed`: владелец
   *  состояния — вызывающий, а компонент только рисует вдавленность и сам
   *  выставляет `aria-pressed`. */
  selected: boolean;
  disabled: boolean;
};

interface CommonOpts {
  size?: BwSize;
  shape?: BwShape;
  /** Классы потребителя — ими кнопку СТАВЯТ (место в ряду), а не красят. */
  cls?: string;
  disabled?: boolean;
  onClick?: () => void;
}

function base(opts: CommonOpts): BwButtonEl {
  const b = document.createElement('bw-button') as BwButtonEl;
  if (opts.size) b.size = opts.size;
  if (opts.shape) b.shape = opts.shape;
  if (opts.cls) b.className = opts.cls;
  if (opts.disabled) b.disabled = true;
  // Обработчик вешается ТОЛЬКО когда он есть: выключенная кнопка края сетлиста
  // остаётся без него вовсе, и это второй замок поверх `disabled`.
  if (opts.onClick) {
    const cb = opts.onClick;
    b.addEventListener('bw-click', () => cb());
  }
  return b;
}

export interface IconButtonOpts extends CommonOpts {
  /** Знак на кнопке: ⤢, ✕, ‹, ›. */
  glyph: string;
  /** Доступное имя И подсказка. Обязательно: подписи у кнопки-глифа нет. */
  name: string;
}

export function iconButton(opts: IconButtonOpts): BwButtonEl {
  const b = base(opts);
  b.title = opts.name;
  b.setAttribute('aria-label', opts.name);

  // Правило 1: глиф в СЛОТ иконки. Свой класс на нём — чтобы задать кегль знака:
  // подпись пуста, значит `--bw-button-font` достаётся глифу как есть, а знак
  // в 12 px внутри капа в 28 px читается мелко.
  const g = document.createElement('span');
  g.setAttribute('slot', 'icon-start');
  g.className = 'cw-glyph';
  g.textContent = opts.glyph;
  // appendChild, а не append(): в этом проекте глобальный Element приходит из
  // @cloudflare/workers-types, у него `append` другой сигнатуры, и tsc её
  // отвергает (тот же шрам у waves.ts и mount.ts).
  b.appendChild(g);
  return b;
}

export interface TextButtonOpts extends CommonOpts {
  /** Видимая подпись. Она же и доступное имя — второго не нужно. */
  text: string;
  /** Растянуть на ширину контейнера (телефон: кнопка чарта во всю ширину). */
  block?: boolean;
  selected?: boolean;
}

export function textButton(opts: TextButtonOpts): BwButtonEl {
  const b = base(opts);
  b.textContent = opts.text;
  if (opts.block) b.block = true;
  if (opts.selected) b.selected = true;
  return b;
}
